import { Button, Flex, Text, TextInput, Modal, Pagination, Loader, createStyles, Tooltip } from "@mantine/core";
import { useStore } from "../../../hooks/use-store";
import { observer } from "mobx-react";
import { useState, useEffect } from "react";
import { useRequest } from "ahooks";
import { NOTIFICATION_SERVICE } from "../../../components/notifications/NotificationService";
import ImageObserved from "./Image";
import { priceMap } from "./Router";
import { notifications } from "@mantine/notifications";
import { MODE_2 } from "../../../stores/data/space-store/space-store";
import { useHistory } from "react-router-dom";

interface Image {
    id: string;
    name: string;
    preview_url: string;
    thumbnail_url: string;
    image_type: 'hero' | 'standard';
}

interface ImageSet {
    id: string;
    name: string;
    description: string;
    hero_image_id: string | null;
    created_at: string;
    images: Image[];
}

interface GalleryResponse {
    sets: ImageSet[];
    page: number;
    pageSize: number;
    totalPages: number;
    totalPhotos: number;
    totalSets: number;
}

const useStyles = createStyles((theme) => ({
    gridContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: '18px',
        width: '100%',
    },
}));


const Gallery = (model: {}) => {
    const { classes } = useStyles();
    const {
        uiStore: { authStore },
        dataStore: { appStore, spaceStore },
    } = useStore();

    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedImageSet, setSelectedImageSet] = useState<ImageSet | null>(null);
    const [modalOpened, setModalOpened] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [hoveredThumbId, setHoveredThumbId] = useState<string | null>(null);
    const [hoveredMain, setHoveredMain] = useState(false);

    const history = useHistory();
    
    const { loading: trainLocationLoading, runAsync: trainLocation } = useRequest(
        async (model: { itemName: string, images: string[] }) => appStore.trainLocation(model),
        {
            manual: true,
            onSuccess: (res) => {
                if (res.error) {
                    NOTIFICATION_SERVICE.errorNotification(
                        "Sorry, service is unavailable due to high demand. Please try later"
                    );
                } else {
                    return res.url;
                }
            },
        }
    );

    const eventHandlers = (id: string) => ({
        onMouseOver: () => setHoveredId(id),
        onMouseOut: () => setHoveredId(null)
    });

    // Helper to get the hero image from an image set
    const getHeroImage = (imageSet: ImageSet): Image | undefined => {
        return imageSet.images.find(img => img.image_type === 'hero') || imageSet.images[0];
    };

    const { data: galleryData, loading: galleryLoading, run: runSearch } = useRequest<GalleryResponse, [number, string]>(
        async (page: number, sq: string) => {
            const response = await appStore.getMagroundPage({
                page: page,
                title: sq || "",
            });
            return response;
        },
        {
            manual: true,
            onError: () => {
                NOTIFICATION_SERVICE.errorNotification("Failed to load gallery");
            },
        }
    );

    useEffect(() => {
        runSearch(currentPage, searchQuery);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const handleSearch = () => {
        setSearchQuery(searchValue);
        setCurrentPage(1);
        runSearch(1, searchValue);
    };

    return (
        <Flex w={'100%'} mt={12} pt={16} pb={16} style={{ borderRadius: '4px' }} gap={32} direction="column">
            <Flex gap={8} justify="center">
                <TextInput
                    color="orange"
                    placeholder="Search images..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.currentTarget.value)}
                    style={{ width: '300px' }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleSearch();
                        }
                    }}
                />
                <Button onClick={handleSearch} color="orange">
                    Search
                </Button>
            </Flex>

            {galleryLoading ? (
                <Flex justify="center" align="center" h="400px">
                    <Loader size="md" color="white" />
                </Flex>
            ) : (
                <>
                    <div className={classes.gridContainer}>
                        {galleryData?.sets?.map((imageSet: ImageSet) => {
                            if (imageSet.images.length < 10) {
                                return null; // Skip sets with less than 10 images
                            }
                            const heroImage = getHeroImage(imageSet);
                            if (!heroImage) return null;
                            return (
                                <Flex
                                    key={imageSet.id}
                                    h="180px"
                                    w="100%"
                                    style={{
                                        cursor: 'pointer',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        borderRadius: '4px'
                                    }}
                                    onClick={() => {
                                        setSelectedImageSet(imageSet);
                                        setModalOpened(true);
                                    }}
                                    {...eventHandlers(imageSet.id)}
                                >
                                    <ImageObserved
                                        width="100%"
                                        height="100%"
                                        src={heroImage.preview_url}
                                        objectFit="cover"
                                        style={{
                                            objectPosition: 'center',
                                            borderRadius: '4px'
                                        }}
                                    />
                                    <Flex
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            padding: '16px',
                                            background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0) 100%)',
                                            opacity: hoveredId === imageSet.id ? 1 : 0,
                                            transition: 'opacity 0.2s ease',
                                            height: '50%',
                                            alignItems: 'flex-end'
                                        }}
                                    >
                                        <Flex direction="column" gap={4}>
                                            <Text color="white" size="sm" weight={600} style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                {imageSet.name || 'Untitled Set'}
                                            </Text>
                                            <Text color="white" size="xs" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                {imageSet.images.length} {imageSet.images.length === 1 ? 'perspective' : 'perspectives'}
                                            </Text>
                                        </Flex>
                                    </Flex>
                                </Flex>
                            )
                        })}
                    </div>

                    {galleryData && galleryData.totalPages > 1 && (
                        <Flex justify="center" mt={20}>
                            <Pagination
                                color="orange"
                                total={galleryData.totalPages}
                                value={currentPage}
                                onChange={setCurrentPage}
                            />
                        </Flex>
                    )}
                </>
            )}

            <Modal
                opened={modalOpened}
                onClose={() => setModalOpened(false)}
                size="auto"

                title={'Location set ' + selectedImageSet?.name}
            >
                {selectedImageSet && (() => {
                    const selectedHeroImage = getHeroImage(selectedImageSet);
                    if (!selectedHeroImage) return null;
                    return (
                    <Flex direction="column" gap={32} maw="90vw" p={32}>
                        <Flex justify="center" gap={32} style={{ maxHeight: '80vh', overflow: 'hidden' }}>
                            <Flex
                                style={{ position: 'relative' }}
                                onMouseEnter={() => setHoveredMain(true)}
                                onMouseLeave={() => setHoveredMain(false)}
                            >
                                <ImageObserved
                                    width="600px"
                                    height="400px"
                                    src={selectedHeroImage.preview_url}
                                    objectFit="contain"
                                    style={{
                                        maxHeight: '80vh',
                                        maxWidth: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                                {authStore.isBasicAndGreater() && (
                                    <Flex
                                        align="center"
                                        justify="center"
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            borderRadius: '4px',
                                            background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0) 100%)',
                                            opacity: hoveredMain ? 1 : 0,
                                            transition: 'opacity 0.15s ease',
                                        }}
                                    >
                                        <Button
                                            color="orange"
                                            onClick={() => {
                                                spaceStore.setSelectedImage({ src: selectedHeroImage.preview_url });
                                                spaceStore.setMode2(MODE_2.PLACE_CAR);
                                                history.push('/generate')
                                                setModalOpened(false)
                                                
                                            }}
                                        >
                                            Select for Place Car
                                        </Button>
                                    </Flex>
                                )}
                            </Flex>
                            <Flex
                                // align={'center'}
                                direction="column"
                                gap={12}
                                style={{
                                    width: '400px',
                                    position: 'relative',
                                    padding: '32px',
                                    backgroundColor: 'rgba(32, 32, 32, 1)',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                }}
                            >
                                <Flex gap={8}>
                                    <Text color="white" size={'md'} fw={700}>
                                        Train Your Own Location LoRA
                                    </Text>
                                </Flex>

                                <Text color="rgba(189, 189, 189, 1)" size={'sm'}>
                                    Train a location LoRA based on this specific environment from the MAGROUND archive with just one click. Perfect for generating consistent image styles, location-based variations, or custom visuals tailored to your creative vision.
                                </Text>
                                <ul style={{ color: 'rgba(189, 189, 189, 1)', fontSize: '14px', margin: '8px 0', paddingLeft: '16px' }}>
                                    <li style={{ marginBottom: '8px' }}>Training time: approx. 20 minutes</li>
                                    <li style={{ marginBottom: '8px' }}>Based on MAGROUND's licensed content</li>
                                    <li style={{ marginBottom: '8px' }}>No coding required – ready to use right after training</li>
                                </ul>
                                <Text color="rgba(189, 189, 189, 1)" size={'sm'}>
                                    Click "Add To My AI Locations" to get started.
                                </Text>
                                <Flex justify="center" mt={16}>
                                    <Tooltip p={12} label={
                                        <span>Train cost: <span style={{
                                            color: '#f53c02',
                                            fontWeight: 700
                                        }}>{priceMap.TRAIN} credits</span>
                                        </span>
                                    }
                                        withArrow
                                        position="right"
                                        color='dark'
                                    >
                                        <span>
                                            <Button
                                                color="orange"
                                                loading={trainLocationLoading}
                                                onClick={async () => {
                                                    if (!(authStore.currentUser?.access_purchased || authStore.currentUser?.subscription_id)) {
                                                        spaceStore.setBillingAccessModalOpen(true)
                                                        setModalOpened(false)
                                                        return
                                                    }

                                                    NOTIFICATION_SERVICE.successNotification('Preparing for training... Please do not close this tab', true, 10000000000)
                                                    await trainLocation({ 
                                                        itemName: selectedHeroImage.name,
                                                        images: selectedImageSet.images.map(img => img.preview_url)
                                                    })
                                                    notifications.clean()
                                                    NOTIFICATION_SERVICE.successNotification('Your custom LoRA model is now being trained with the selected location. This will take approximately 20 minutes to complete. We\'ll notify you once the training is finished. You can leave this page and continue.')
                                                    authStore.syncUser();

                                                }}
                                                disabled={selectedImageSet.images.length < 10}
                                            >
                                                Add To My AI Locations 
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Flex>
                            </Flex>


                        </Flex>
                        <Flex wrap="wrap" gap={16} justify="center">
                            {selectedImageSet.images.map((image) => (
                                <Flex
                                    key={image.id}
                                    style={{ position: 'relative', borderRadius: '4px' }}
                                    onMouseEnter={() => setHoveredThumbId(image.id)}
                                    onMouseLeave={() => setHoveredThumbId(null)}
                                >
                                    <ImageObserved
                                        width="170px"
                                        height="127px"
                                        src={image.thumbnail_url}
                                        objectFit="cover"
                                        style={{ borderRadius: '4px' }}
                                    />

                                    {authStore.isBasicAndGreater() && (
                                        <Flex
                                            align="center"
                                            justify="center"
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                borderRadius: '4px',
                                                background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0) 100%)',
                                                opacity: hoveredThumbId === image.id ? 1 : 0,
                                                transition: 'opacity 0.15s ease',
                                            }}
                                        >
                                            <Button
                                                color="orange"
                                                size="xs"
                                                onClick={() => {
                                                    spaceStore.setSelectedImage({ src: image.preview_url });
                                                    spaceStore.setMode2(MODE_2.PLACE_CAR);
                                                    history.push('/generate')
                                                    setModalOpened(false)

                                                }}
                                            >
                                                Select for Place Car
                                            </Button>
                                        </Flex>
                                    )}
                                </Flex>
                            ))}
                        </Flex>

                    </Flex>
                    );
                })()}
            </Modal>
        </Flex>
    );
}

export default observer(Gallery);